import React, { createContext, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Crown, X, Check, Bell, Bookmark, BarChart3, LineChart } from 'lucide-react';
import { Button } from '../components/ui/button';

/**
 * ProUpsellContext — soft upsell modal globale per feature Pro.
 *
 * Sostituisce il toast.error rosso "PRO_REQUIRED" con un modale chiaro,
 * positivo e orientato alla conversione. Esposto via hook `useProUpsell()`.
 *
 * Usage:
 *   const { showProUpsell } = useProUpsell();
 *   showProUpsell('alert'); // reason: 'alert' | 'saved' | 'compare' | 'analytics'
 */

const ProUpsellContext = createContext(null);

export function useProUpsell() {
  return useContext(ProUpsellContext) || { showProUpsell: () => {} };
}

const REASONS = {
  alert: { icon: Bell, titleKey: 'proUpsellAlertTitle', descKey: 'proUpsellAlertDesc' },
  saved: { icon: Bookmark, titleKey: 'proUpsellSavedTitle', descKey: 'proUpsellSavedDesc' },
  compare: { icon: BarChart3, titleKey: 'proUpsellCompareTitle', descKey: 'proUpsellCompareDesc' },
  analytics: { icon: BarChart3, titleKey: 'proUpsellAnalyticsTitle', descKey: 'proUpsellAnalyticsDesc' },
  price_history: { icon: LineChart, titleKey: 'proUpsellPriceHistoryTitle', descKey: 'proUpsellPriceHistoryDesc' },
};

export function ProUpsellProvider({ children }) {
  const [reason, setReason] = useState(null);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const showProUpsell = useCallback((r = 'alert') => setReason(r), []);
  const close = useCallback(() => setReason(null), []);

  const cfg = reason ? (REASONS[reason] || REASONS.alert) : null;
  const Icon = cfg?.icon;

  // Benefits ordinati in modo context-aware: il beneficio pertinente al reason
  // è mostrato per primo, con un accent visivo ("↑ per te"), per rafforzare
  // la CTA di upsell nel punto esatto in cui l'utente ha incontrato il limite.
  const BENEFIT_KEYS = {
    unlimited:    'proUpsellBenefitUnlimited',
    alerts:       'proUpsellBenefitAlerts',
    priceHistory: 'proUpsellBenefitPriceHistory',
    noAds:        'proUpsellBenefitNoAds',
  };
  const DEFAULT_ORDER = ['unlimited', 'alerts', 'priceHistory', 'noAds'];
  const HIGHLIGHT_BY_REASON = {
    alert:         'alerts',
    saved:         'alerts',        // salvataggi → limite saved → alert comunicano estensione del valore
    compare:       'priceHistory',
    analytics:     'priceHistory',
    price_history: 'priceHistory',
  };
  const highlightKey = reason ? (HIGHLIGHT_BY_REASON[reason] || 'unlimited') : 'unlimited';
  const orderedKeys = [highlightKey, ...DEFAULT_ORDER.filter(k => k !== highlightKey)];

  return (
    <ProUpsellContext.Provider value={{ showProUpsell }}>
      {children}
      {reason && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
          onClick={close}
          data-testid="pro-upsell-backdrop"
        >
          <div
            className="bg-background border border-border rounded-2xl p-6 w-full max-w-sm shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="pro-upsell-modal"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center">
                  {Icon && <Icon size={20} className="text-amber-600" />}
                </div>
                <div>
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-600 bg-amber-500/10 border border-amber-500/20 rounded-full px-2 py-0.5">
                    <Crown size={10} /> Pro
                  </span>
                  <h3 className="font-bold text-base mt-1" data-testid="pro-upsell-title">
                    {t(cfg.titleKey)}
                  </h3>
                </div>
              </div>
              <button
                onClick={close}
                className="p-1.5 hover:bg-muted rounded-lg text-muted-foreground"
                data-testid="pro-upsell-close-btn"
              >
                <X size={16} />
              </button>
            </div>

            <p className="text-sm text-muted-foreground mb-4" data-testid="pro-upsell-desc">
              {t(cfg.descKey)}
            </p>

            <ul className="space-y-2 mb-5" data-testid="pro-upsell-benefits">
              {orderedKeys.map((k, i) => {
                const isHighlight = i === 0;
                return (
                  <li
                    key={k}
                    data-testid={`pro-upsell-benefit-${k}`}
                    className={`flex items-start gap-2 text-sm rounded-lg ${isHighlight ? 'bg-amber-500/10 border border-amber-500/20 px-2.5 py-2 -mx-0.5' : ''}`}
                  >
                    <Check size={16} className={`${isHighlight ? 'text-amber-600' : 'text-emerald-500'} mt-0.5 shrink-0`} />
                    <span className={isHighlight ? 'font-semibold' : ''}>{t(BENEFIT_KEYS[k])}</span>
                    {isHighlight && (
                      <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-amber-600 shrink-0">
                        {t('proUpsellForYou') || '↑ per te'}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>

            <div className="text-center mb-4">
              <span className="text-3xl font-bold">CHF 9.90</span>
              <span className="text-sm text-muted-foreground">/{t('month') || 'mese'}</span>
            </div>

            <Button
              onClick={() => { close(); navigate('/pro'); }}
              className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold"
              data-testid="pro-upsell-cta-btn"
            >
              {t('proUpsellCta')}
            </Button>

            <button
              onClick={close}
              className="w-full mt-2 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
              data-testid="pro-upsell-later-btn"
            >
              {t('proUpsellLater')}
            </button>
          </div>
        </div>
      )}
    </ProUpsellContext.Provider>
  );
}
